import { Clock, AlertCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { withErrorCode } from '@/lib/errors'
import { useCountdown } from '../hooks/useCountdown'
import type { ApplicationDocument } from '@/types'

import { formatApplicationDeadline } from '../lib/deadlineFormatting'

const ONE_DAY_MS = 24 * 60 * 60 * 1000

interface Props {
  application: ApplicationDocument & { id: string }
}

export function DeadlineBanner({ application }: Props) {
  const deadline: Date | null = application.deadline
    ? (application.deadline as unknown as { toDate(): Date }).toDate?.() ??
      new Date(application.deadline as unknown as string)
    : null

  const { hours, minutes, seconds, expired } = useCountdown(deadline)

  if (application.status === 'closed') {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="font-medium">
          {withErrorCode(
            'Applications for this opportunity are now closed.',
            'applications/closed',
          )}
        </AlertDescription>
      </Alert>
    )
  }

  if (application.status === 'draft') {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="font-medium">
          {withErrorCode(
            'This application is not yet open for submissions. Please check back soon.',
            'applications/not-open',
          )}
        </AlertDescription>
      </Alert>
    )
  }

  if (!deadline) return null

  const msLeft = deadline.getTime() - Date.now()

  if (msLeft <= 0 || expired) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="font-medium">
          {withErrorCode(
            'The deadline has passed. Submissions are no longer accepted.',
            'applications/deadline-passed',
          )}
        </AlertDescription>
      </Alert>
    )
  }

  if (msLeft <= ONE_DAY_MS) {
    // Countdown mode
    const pad = (n: number) => String(n).padStart(2, '0')
    return (
      <Alert>
        <Clock className="h-4 w-4" />
        <AlertDescription>
          <span className="font-medium">Closing soon — </span>
          <span className="font-mono text-sm">
            {pad(hours)}:{pad(minutes)}:{pad(seconds)}
          </span>{' '}
          remaining
          <span className="block text-sm">{formatApplicationDeadline(deadline)}</span>
        </AlertDescription>
      </Alert>
    )
  }

  // Show formatted deadline date
  return (
    <Alert>
      <Clock className="h-4 w-4" />
      <AlertDescription>
        <span className="font-medium">Application deadline: </span>
        {formatApplicationDeadline(deadline)}
      </AlertDescription>
    </Alert>
  )
}
